import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { Accordion } from '@abyss/web/ui/Accordion';
import { Drawer } from '@abyss/web/ui/Drawer';
import { Heading } from '@abyss/web/ui/Heading';
import { Text } from '@abyss/web/ui/Text';
import { find } from 'lodash';
import { useTranslation } from 'react-i18next';

import { useLagoon } from '../../../../hooks/useLagoon';
import { AreaOfExpertiseDetails } from '../../../../models/ProviderDetails';
import { capitalizeEachWord } from '../../../../utils/providerNames.utils';
import { ConstantsLagoon } from '../../../ConstantsLagoon';
import {
  phoneOnly,
  recentActivityDrawerDesktop,
  recentActivityDrawerMobile,
} from '../../../ConstantsStyles';
import { CollapseCardHeaderStyle } from '../About.styled';

type Props = {
  areaOfExpertiseDetails?: AreaOfExpertiseDetails[];
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
};

export const AoeDrawer = ({
  areaOfExpertiseDetails,
  isOpen,
  setIsOpen,
}: Props) => {
  const { t } = useTranslation();
  const mobileScreen = useMediaQuery(phoneOnly);
  const lagoonData = useLagoon('ui-messaging')();
  const aoeDescriptionData = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.AOE_DESCRIPTION_DRAWER_SUBTITLE,
  });

  return (
    <Drawer
      css={
        mobileScreen ? recentActivityDrawerMobile : recentActivityDrawerDesktop
      }
      data-testid="drawer-aoe-description"
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      position={mobileScreen ? 'bottom' : 'right'}
      size={mobileScreen ? window.innerHeight - 104 : 408}
      title={
        <Heading
          color="#002677"
          data-auto-testid="aoe-drawer-heading"
          data-testid="aoe-drawer-heading"
          offset={3}
        >
          {t('PROVIDER_DETAILS.ABOUT_TAB.DETAILS_SECTION.AREAS_OF_EXPERTISE')}
        </Heading>
      }
    >
      <Drawer.Section>
        <Text
          color="#4B4D4F"
          css={{
            'abyss-text-root': { paddingBottom: '16px', display: 'block' },
          }}
          size={16}
        >
          {aoeDescriptionData?.message}
        </Text>
        <Accordion
          css={{
            ...(CollapseCardHeaderStyle() as any),
            margin: '0 -16px',
            width: 'calc(100% + 32px)',
            marginBottom: '16px',
            '.abyss-accordion-trigger': {
              padding: '16px',
              minHeight: '56px',
              boxSizing: 'border-box',
              backgroundColor: 'transparent',
              borderBottom: '1px solid #CBCCCD',
            },
            '.abyss-accordion-trigger[aria-expanded="true"]': {
              backgroundColor: '#EDF3FB',
            },
            '.abyss-accordion-item[data-state="open"] .abyss-accordion-trigger': {
              backgroundColor: '#EDF3FB',
            },
          }}
          defaultValue={`${areaOfExpertiseDetails?.[0]?.name}-0`}
          isCollapsible
        >
          {areaOfExpertiseDetails &&
            areaOfExpertiseDetails?.map((item, index) => (
              <Accordion.Item key={`${item.name}-${index}`} value={`${item.name}-${index}`}>
                <Accordion.Trigger>
                  <Accordion.Header>
                    {capitalizeEachWord(item.name)}
                  </Accordion.Header>
                </Accordion.Trigger>
                <Accordion.Content>
                  <div
                    style={{
                      padding: '12px 16px 16px',
                      borderBottom: '1px solid #CBCCCD',
                    }}
                  >
                    <Text
                      color="#4B4D4F"
                      css={{ 'abyss-text-root': { lineHeight: '24px' } }}
                      size={16}
                    >
                      {item?.description}
                    </Text>
                  </div>
                </Accordion.Content>
              </Accordion.Item>
            ))}
        </Accordion>
      </Drawer.Section>
    </Drawer>
  );
};
